import React, { useEffect, useState, useCallback } from 'react';
import {
  ChevronRight, ChevronDown, CheckCircle, X, Plus,
  Edit2, Trash2, Save, RefreshCw, Flag, Shield,
  Camera, Clock, ArrowUp, ArrowDown, Package,
  Users, Truck, Settings, AlertTriangle, Search, Layers
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────
interface Project { project_id: number; project_code: string; project_name: string; building_type: string; status: string; }
interface Activity {
  plan_id: number; activity_id: number; act_code?: string; activity_name: string;
  activity_type: string; status: string; completion_pct: number;
  is_applicable: boolean; is_milestone: boolean; is_custom_activity: boolean;
  is_critical_path: boolean; photo_required: string; quality_check_required: boolean;
  planned_start_date: string | null; planned_end_date: string | null;
  actual_start_date: string | null; actual_end_date: string | null;
  duration_expected: number; duration_optimistic: number; duration_likely: number;
  duration_pessimistic: number; sequence_order: number;
  assigned_role: string | null;
  predecessor_plan_ids: number[] | null; notes: string | null;
}
interface SubProcess {
  sub_process_id: number; sub_process_code: string; sub_process_name: string;
  sequence_order: number; activities: Activity[];
  activity_count: number; applicable_count: number;
}
interface Process {
  process_id: number; process_code: string; process_name: string;
  sequence_order: number; sub_processes: SubProcess[];
  activity_count: number; applicable_count: number;
}
interface Stage {
  stage_id: number; stage_code: string; stage_name: string; sequence_order: number;
  is_repeatable: boolean; billing_trigger: boolean; customer_visible: boolean;
  processes: Process[];
  activity_count: number; applicable_count: number; completed_count: number;
}

const TYPE_COLORS: Record<string, string> = {
  STRUCTURAL: 'bg-blue-100 text-blue-700', FINISHING: 'bg-amber-100 text-amber-700',
  MEP: 'bg-cyan-100 text-cyan-700', EXTERNAL: 'bg-green-100 text-green-700', ADMIN: 'bg-gray-100 text-gray-600',
};
const STATUS_COLORS: Record<string, string> = {
  NOT_STARTED: 'bg-gray-100 text-gray-500', IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-green-100 text-green-700', DELAYED: 'bg-red-100 text-red-700',
  BLOCKED: 'bg-orange-100 text-orange-700',
};
const BLDG_COLORS: Record<string, string> = {
  IH: 'bg-blue-500', VI: 'bg-purple-500', SB: 'bg-cyan-500',
  MB: 'bg-indigo-500', HR: 'bg-rose-500', BP: 'bg-amber-500', TW: 'bg-green-500',
};
const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

// ── Add Stage Modal ───────────────────────────────────────────
const AddStageModal = ({ existingStages, onClose, onSaved }: {
  existingStages: PosItem[]; onClose: () => void; onSaved: () => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    stage_name: '', description: '',
    applicable_bldg: 'ALL',
    is_repeatable: false, is_block_level: false, is_floor_level: false,
    is_project_global: false, is_template_stage: false,
    billing_trigger: false, customer_visible: false,
    after_stage_id: '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.stage_name.trim()) return setError('Stage Name is required');
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post('/activity/stages', {
        ...form,
        after_stage_id: form.after_stage_id ? Number(form.after_stage_id) : null,
      });
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to add stage'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Layers size={16} className="text-blue-600" />Add New Stage</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Position (insert after)</label>
            <select value={form.after_stage_id} onChange={e => set('after_stage_id', e.target.value)} className={inp}>
              <option value="">— At beginning —</option>
              {existingStages.map(s => (
                <option key={s.id} value={s.id}>After: {s.code} {s.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Leave blank to place at beginning; select last stage to append at end</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Stage Name *</label>
            <input required autoFocus value={form.stage_name} onChange={e => set('stage_name', e.target.value)}
              placeholder="e.g. Foundation Work" className={inp} />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Optional..." className={inp} />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Applicable Building Type</label>
            <select value={form.applicable_bldg} onChange={e => set('applicable_bldg', e.target.value)} className={inp}>
              {['ALL','IH','VI','SB','MB','HR','BP','TW'].map(b => <option key={b} value={b}>{b === 'ALL' ? 'ALL — Every building type' : b}</option>)}
            </select>
          </div>

          <div className="border border-gray-100 rounded-xl p-3">
            <p className="text-xs font-semibold text-gray-500 mb-2">Stage Flags</p>
            <div className="grid grid-cols-2 gap-y-2">
              {[
                { k: 'is_repeatable',    l: 'Repeatable (floor/block)' },
                { k: 'is_block_level',   l: 'Block Level Stage' },
                { k: 'is_floor_level',   l: 'Floor Level Stage' },
                { k: 'is_project_global',l: 'Project Global' },
                { k: 'billing_trigger',  l: 'Billing Trigger' },
                { k: 'customer_visible', l: 'Customer Visible' },
              ].map(c => (
                <label key={c.k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={(form as any)[c.k]} onChange={e => set(c.k, e.target.checked)} className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700">{c.l}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Stage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Add Process Modal ─────────────────────────────────────────
const AddProcessModal = ({ stageId, stageName, existingProcesses, onClose, onSaved }: {
  stageId: number; stageName: string;
  existingProcesses: PosItem[];
  onClose: () => void; onSaved: () => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    process_name: '', description: '',
    applicable_bldg: 'ALL',
    after_process_id: existingProcesses.length ? String(existingProcesses[existingProcesses.length - 1].id) : '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.process_name.trim()) return setError('Process Name is required');
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post('/activity/processes/by-id', {
        ...form, stage_id: stageId,
        after_process_id: form.after_process_id ? Number(form.after_process_id) : null,
      });
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to add process'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Plus size={16} className="text-indigo-600" />Add Process</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div className="bg-indigo-50 rounded-lg px-3 py-2 text-sm text-indigo-800">
            Stage: <span className="font-semibold">{stageName}</span>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Position (insert after)</label>
            <select value={form.after_process_id} onChange={e => set('after_process_id', e.target.value)} className={inp}>
              <option value="">— At beginning of stage —</option>
              {existingProcesses.map(p => (
                <option key={p.id} value={p.id}>After: {p.code} {p.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Select the last process to append at end (default)</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Process Name *</label>
            <input required autoFocus value={form.process_name} onChange={e => set('process_name', e.target.value)}
              placeholder="e.g. Column Construction" className={inp} />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Applicable Building Type</label>
            <select value={form.applicable_bldg} onChange={e => set('applicable_bldg', e.target.value)} className={inp}>
              {['ALL','IH','VI','SB','MB','HR','BP','TW'].map(b => <option key={b} value={b}>{b === 'ALL' ? 'ALL — Every building type' : b}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Optional description..." className={inp} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Process'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Add Sub-Process Modal ─────────────────────────────────────
const AddSubProcessModal = ({ processId, processName, stageName, existingSubProcesses, onClose, onSaved }: {
  processId: number; processName: string; stageName: string;
  existingSubProcesses: PosItem[];
  onClose: () => void; onSaved: () => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    sub_process_name: '', description: '',
    after_sub_process_id: existingSubProcesses.length ? String(existingSubProcesses[existingSubProcesses.length - 1].id) : '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.sub_process_name.trim()) return setError('Sub-Process Name is required');
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post('/activity/sub-processes/by-id', {
        ...form, process_id: processId,
        after_sub_process_id: form.after_sub_process_id ? Number(form.after_sub_process_id) : null,
      });
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to add sub-process'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Plus size={16} className="text-teal-600" />Add Sub-Process</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          <div className="bg-teal-50 rounded-lg px-3 py-2 text-sm text-teal-800 space-y-0.5">
            <div>Stage: <span className="font-semibold">{stageName}</span></div>
            <div>Process: <span className="font-semibold">{processName}</span></div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Position (insert after)</label>
            <select value={form.after_sub_process_id} onChange={e => set('after_sub_process_id', e.target.value)} className={inp}>
              <option value="">— At beginning of process —</option>
              {existingSubProcesses.map(sp => (
                <option key={sp.id} value={sp.id}>After: {sp.code} {sp.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Select the last sub-process to append at end (default)</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Sub-Process Name *</label>
            <input required autoFocus value={form.sub_process_name} onChange={e => set('sub_process_name', e.target.value)}
              placeholder="e.g. Shuttering" className={inp} />
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Optional description..." className={inp} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Sub-Process'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Add Custom Activity Modal ─────────────────────────────────
const AddActivityModal = ({ projectId, stages, onClose, onSaved, preStageId, preProcessId, preSubProcessId }: {
  projectId: number; stages: Stage[]; onClose: () => void; onSaved: () => void;
  preStageId?: number; preProcessId?: number; preSubProcessId?: number;
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [processes, setProcesses] = useState<any[]>([]);
  const [subProcesses, setSubProcesses] = useState<any[]>([]);
  const [form, setForm] = useState({
    stage_id: preStageId ? String(preStageId) : '',
    process_id: preProcessId ? String(preProcessId) : '',
    sub_process_id: preSubProcessId ? String(preSubProcessId) : '',
    activity_name: '', activity_type: 'ADMIN',
    duration_optimistic: 1, duration_likely: 1, duration_pessimistic: 2,
    planned_start_date: '', planned_end_date: '',
    photo_required: 'OPTIONAL', quality_check_required: false,
    is_milestone: false, assigned_role: '', notes: '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (preStageId) {
      const stage = stages.find(s => s.stage_id === preStageId);
      setProcesses(stage?.processes || []);
    }
  }, []);

  useEffect(() => {
    if (preProcessId && preStageId) {
      const stage = stages.find(s => s.stage_id === preStageId);
      const proc = stage?.processes.find(p => p.process_id === preProcessId);
      setSubProcesses(proc?.sub_processes || []);
    }
  }, []);

  useEffect(() => {
    if (form.stage_id && !preStageId) {
      const stage = stages.find(s => s.stage_id === Number(form.stage_id));
      setProcesses(stage?.processes || []);
      setForm(f => ({ ...f, process_id: '', sub_process_id: '' }));
    }
  }, [form.stage_id]);

  useEffect(() => {
    if (form.process_id && !preProcessId) {
      const stage = stages.find(s => s.stage_id === Number(form.stage_id));
      const proc = stage?.processes.find(p => p.process_id === Number(form.process_id));
      setSubProcesses(proc?.sub_processes || []);
      setForm(f => ({ ...f, sub_process_id: '' }));
    }
  }, [form.process_id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.stage_id || !form.process_id || !form.sub_process_id || !form.activity_name)
      return setError('Stage, Process, Sub-Process and Activity Name are required');
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/project-plan/${projectId}/custom-activity`, {
        stage_id: Number(form.stage_id), process_id: Number(form.process_id),
        sub_process_id: Number(form.sub_process_id),
        activity_name: form.activity_name, activity_type: form.activity_type,
        duration_optimistic: form.duration_optimistic, duration_likely: form.duration_likely,
        duration_pessimistic: form.duration_pessimistic,
        planned_start_date: form.planned_start_date || null,
        planned_end_date: form.planned_end_date || null,
        photo_required: form.photo_required,
        quality_check_required: form.quality_check_required,
        is_milestone: form.is_milestone,
        assigned_role: form.assigned_role || null,
        notes: form.notes || null,
      });
      onSaved();
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to add activity'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">Add Custom Activity</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Stage *</label>
            <select required value={form.stage_id} onChange={e => set('stage_id', e.target.value)} className={inp} disabled={!!preStageId}>
              <option value="">Select stage...</option>
              {stages.map(s => <option key={s.stage_id} value={s.stage_id}>{s.stage_code} — {s.stage_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Process *</label>
            <select required value={form.process_id} onChange={e => set('process_id', e.target.value)} className={inp} disabled={!form.stage_id || !!preProcessId}>
              <option value="">Select process...</option>
              {processes.map((p: any) => <option key={p.process_id} value={p.process_id}>{p.process_code} — {p.process_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Sub-Process *</label>
            <select required value={form.sub_process_id} onChange={e => set('sub_process_id', e.target.value)} className={inp} disabled={!form.process_id || !!preSubProcessId}>
              <option value="">Select sub-process...</option>
              {subProcesses.map((sp: any) => <option key={sp.sub_process_id} value={sp.sub_process_id}>{sp.sub_process_code} — {sp.sub_process_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Activity Name *</label>
            <input required value={form.activity_name} onChange={e => set('activity_name', e.target.value)} placeholder="e.g. Special waterproofing coat" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Type</label>
              <select value={form.activity_type} onChange={e => set('activity_type', e.target.value)} className={inp}>
                {['STRUCTURAL', 'FINISHING', 'MEP', 'EXTERNAL', 'ADMIN'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Assigned Role</label>
              <input value={form.assigned_role} onChange={e => set('assigned_role', e.target.value)} placeholder="SITE_ENG, PM..." className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[{ k: 'duration_optimistic', l: 'Best (days)' }, { k: 'duration_likely', l: 'Likely (days)' }, { k: 'duration_pessimistic', l: 'Worst (days)' }].map(d => (
              <div key={d.k}>
                <label className="text-xs font-medium text-gray-600 block mb-1">{d.l}</label>
                <input type="number" min={0.5} step={0.5} value={(form as any)[d.k]}
                  onChange={e => set(d.k, Number(e.target.value))} className={inp} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Planned Start</label>
              <input type="date" value={form.planned_start_date} onChange={e => set('planned_start_date', e.target.value)} className={inp} /></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Planned End</label>
              <input type="date" value={form.planned_end_date} onChange={e => set('planned_end_date', e.target.value)} className={inp} /></div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Photo Required</label>
            <select value={form.photo_required} onChange={e => set('photo_required', e.target.value)} className={inp}>
              {['OPTIONAL', 'MANDATORY', 'NOT_REQUIRED'].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex gap-4">
            {[{ k: 'quality_check_required', l: 'QC Gate' }, { k: 'is_milestone', l: 'Milestone' }].map(c => (
              <label key={c.k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(form as any)[c.k]} onChange={e => set(c.k, e.target.checked)} className="w-4 h-4 rounded" />
                <span className="text-sm text-gray-700">{c.l}</span>
              </label>
            ))}
          </div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} className={inp} /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Adding...' : 'Add Activity'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Activity Edit Drawer ──────────────────────────────────────
const ActivityDrawer = ({ activity, projectId, onClose, onSaved }: {
  activity: Activity; projectId: number; onClose: () => void; onSaved: () => void;
}) => {
  const [tab, setTab] = useState<'details' | 'dates' | 'materials' | 'manpower' | 'machinery'>('details');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [materials, setMaterials] = useState<any[]>([]);
  const [manpower, setManpower] = useState<any[]>([]);
  const [machinery, setMachinery] = useState<any[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [form, setForm] = useState({
    activity_name: activity.activity_name,
    activity_type: activity.activity_type,
    assigned_role: activity.assigned_role || '',
    photo_required: activity.photo_required,
    quality_check_required: activity.quality_check_required,
    is_milestone: activity.is_milestone,
    is_critical_path: activity.is_critical_path,
    notes: activity.notes || '',
    duration_optimistic: activity.duration_optimistic,
    duration_likely: activity.duration_likely,
    duration_pessimistic: activity.duration_pessimistic,
    planned_start_date: activity.planned_start_date?.split('T')[0] || '',
    planned_end_date: activity.planned_end_date?.split('T')[0] || '',
    actual_start_date: activity.actual_start_date?.split('T')[0] || '',
    actual_end_date: activity.actual_end_date?.split('T')[0] || '',
    predecessor_plan_ids: activity.predecessor_plan_ids?.join(', ') || '',
  });
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const loadTabData = useCallback(async (t: string) => {
    if (t === 'materials' && materials.length === 0) {
      setLoadingTab(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get(`/project-plan/${projectId}/activity/${activity.plan_id}/materials`);
        setMaterials(r.data.data || []);
      } finally { setLoadingTab(false); }
    }
    if (t === 'manpower' && manpower.length === 0) {
      setLoadingTab(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get(`/project-plan/${projectId}/activity/${activity.plan_id}/manpower`);
        setManpower(r.data.data || []);
      } finally { setLoadingTab(false); }
    }
    if (t === 'machinery' && machinery.length === 0) {
      setLoadingTab(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get(`/project-plan/${projectId}/activity/${activity.plan_id}/machinery`);
        setMachinery(r.data.data || []);
      } finally { setLoadingTab(false); }
    }
  }, [activity.plan_id, projectId, materials.length, manpower.length, machinery.length]);

  useEffect(() => { loadTabData(tab); }, [tab]);

  const autoLoadMaterials = async () => {
    if (!activity.activity_id) return;
    setSaving(true);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/project-plan/${projectId}/activity/${activity.plan_id}/auto-materials`, {
        activity_id: activity.activity_id, planned_qty: null,
      });
      const r = await api.get(`/project-plan/${projectId}/activity/${activity.plan_id}/materials`);
      setMaterials(r.data.data || []);
    } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { default: api } = await import('../../utils/api');
      await api.put(`/project-plan/activity/${activity.plan_id}`, {
        ...form,
        planned_start_date: form.planned_start_date || null,
        planned_end_date: form.planned_end_date || null,
        actual_start_date: form.actual_start_date || null,
        actual_end_date: form.actual_end_date || null,
        predecessor_plan_ids: form.predecessor_plan_ids
          ? form.predecessor_plan_ids.split(',').map((s: string) => parseInt(s.trim())).filter(Boolean)
          : null,
      });
      setEditing(false);
      onSaved();
    } finally { setSaving(false); }
  };

  const deleteActivity = async () => {
    if (!activity.is_custom_activity) return;
    if (!window.confirm('Delete this custom activity?')) return;
    const { default: api } = await import('../../utils/api');
    await api.delete(`/project-plan/activity/${activity.plan_id}`);
    onSaved();
    onClose();
  };

  const TABS = [
    { key: 'details', label: 'Details', icon: <Settings size={11} /> },
    { key: 'dates', label: 'Dates / PERT', icon: <Clock size={11} /> },
    { key: 'materials', label: 'Materials', icon: <Package size={11} /> },
    { key: 'manpower', label: 'Labour', icon: <Users size={11} /> },
    { key: 'machinery', label: 'Machinery', icon: <Truck size={11} /> },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-gray-400">{activity.act_code}</span>
                {activity.is_custom_activity && <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Custom</span>}
                {activity.is_milestone && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Flag size={9} />Milestone</span>}
                {activity.is_critical_path && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">⚡ Critical</span>}
              </div>
              <h3 className="text-sm font-bold text-gray-900 mt-0.5 leading-snug">{activity.activity_name}</h3>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
              {!editing && <button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={14} /></button>}
              {activity.is_custom_activity && <button onClick={deleteActivity} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>}
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[activity.activity_type] || ''}`}>{activity.activity_type}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[activity.status] || ''}`}>{activity.status.replace('_', ' ')}</span>
            <span className="text-xs text-gray-400">{activity.completion_pct}% done</span>
          </div>
        </div>

        <div className="flex gap-1 px-4 pt-2 border-b border-gray-100 flex-shrink-0 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap
                ${tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'details' && (
            editing ? (
              <div className="space-y-3">
                <div><label className="text-xs font-medium text-gray-600 block mb-1">Activity Name</label>
                  <input value={form.activity_name} onChange={e => set('activity_name', e.target.value)} className={inp} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-gray-600 block mb-1">Type</label>
                    <select value={form.activity_type} onChange={e => set('activity_type', e.target.value)} className={inp}>
                      {['STRUCTURAL', 'FINISHING', 'MEP', 'EXTERNAL', 'ADMIN'].map(t => <option key={t}>{t}</option>)}
                    </select></div>
                  <div><label className="text-xs font-medium text-gray-600 block mb-1">Assigned Role</label>
                    <input value={form.assigned_role} onChange={e => set('assigned_role', e.target.value)} placeholder="SITE_ENG, PM..." className={inp} /></div>
                </div>
                <div><label className="text-xs font-medium text-gray-600 block mb-1">Photo Required</label>
                  <select value={form.photo_required} onChange={e => set('photo_required', e.target.value)} className={inp}>
                    {['OPTIONAL', 'MANDATORY', 'NOT_REQUIRED'].map(p => <option key={p}>{p}</option>)}
                  </select></div>
                <div className="flex flex-wrap gap-4">
                  {[{ k: 'quality_check_required', l: 'QC Gate' }, { k: 'is_milestone', l: 'Milestone' }, { k: 'is_critical_path', l: 'Critical Path' }].map(c => (
                    <label key={c.k} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={(form as any)[c.k]} onChange={e => set(c.k, e.target.checked)} className="w-4 h-4 rounded" />
                      <span className="text-sm text-gray-700">{c.l}</span>
                    </label>
                  ))}
                </div>
                <div><label className="text-xs font-medium text-gray-600 block mb-1">Notes</label>
                  <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className={inp} /></div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setEditing(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg">Cancel</button>
                  <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50">
                    <Save size={14} />{saving ? 'Saving...' : 'Save'}</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Assigned Role', val: activity.assigned_role || 'Not set' },
                    { label: 'Photo Required', val: activity.photo_required },
                    { label: 'QC Gate', val: activity.quality_check_required ? 'Yes' : 'No' },
                    { label: 'Progress', val: `${activity.completion_pct}%` },
                  ].map(s => (
                    <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2">
                      <p className="text-xs text-gray-400">{s.label}</p>
                      <p className="text-sm font-medium text-gray-800">{s.val}</p>
                    </div>
                  ))}
                </div>
                {activity.notes && (
                  <div className="bg-amber-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400 mb-1">Notes</p>
                    <p className="text-sm text-gray-700">{activity.notes}</p>
                  </div>
                )}
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Completion</span><span>{activity.completion_pct}%</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${activity.completion_pct}%` }} />
                  </div>
                </div>
              </div>
            )
          )}

          {tab === 'dates' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-700 mb-3 flex items-center gap-1"><Clock size={12} />PERT Duration</p>
                {editing ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[{ k: 'duration_optimistic', l: 'Best (O)' }, { k: 'duration_likely', l: 'Likely (M)' }, { k: 'duration_pessimistic', l: 'Worst (P)' }].map(d => (
                      <div key={d.k}>
                        <label className="text-xs font-medium text-gray-600 block mb-1">{d.l}</label>
                        <input type="number" min={0.5} step={0.5} value={(form as any)[d.k]}
                          onChange={e => set(d.k, Number(e.target.value))} className={inp} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[
                      { label: 'Best', val: activity.duration_optimistic },
                      { label: 'Likely', val: activity.duration_likely },
                      { label: 'Worst', val: activity.duration_pessimistic },
                      { label: 'Expected', val: activity.duration_expected },
                    ].map(d => (
                      <div key={d.label} className="bg-white rounded-lg py-2">
                        <p className="text-sm font-bold text-gray-800">{d.val || '—'}</p>
                        <p className="text-xs text-gray-400">{d.label}</p>
                      </div>
                    ))}
                  </div>
                )}
                {!editing && (
                  <p className="text-xs text-blue-600 mt-2 text-center">
                    Formula: (O + 4M + P) / 6 = <strong>{activity.duration_expected} days</strong>
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 mb-2">Planned Schedule</p>
                {editing ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-medium text-gray-600 block mb-1">Planned Start</label>
                      <input type="date" value={form.planned_start_date} onChange={e => set('planned_start_date', e.target.value)} className={inp} /></div>
                    <div><label className="text-xs font-medium text-gray-600 block mb-1">Planned End</label>
                      <input type="date" value={form.planned_end_date} onChange={e => set('planned_end_date', e.target.value)} className={inp} /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Planned Start', val: activity.planned_start_date?.split('T')[0] || 'Not set' },
                      { label: 'Planned End', val: activity.planned_end_date?.split('T')[0] || 'Not set' },
                    ].map(s => (
                      <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-400">{s.label}</p>
                        <p className="text-sm font-medium text-gray-800">{s.val}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 mb-2">Actual Progress</p>
                {editing ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-medium text-gray-600 block mb-1">Actual Start</label>
                      <input type="date" value={form.actual_start_date} onChange={e => set('actual_start_date', e.target.value)} className={inp} /></div>
                    <div><label className="text-xs font-medium text-gray-600 block mb-1">Actual End</label>
                      <input type="date" value={form.actual_end_date} onChange={e => set('actual_end_date', e.target.value)} className={inp} /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Actual Start', val: activity.actual_start_date?.split('T')[0] || '—' },
                      { label: 'Actual End', val: activity.actual_end_date?.split('T')[0] || '—' },
                    ].map(s => (
                      <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-400">{s.label}</p>
                        <p className="text-sm font-medium text-gray-800">{s.val}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 mb-2">Dependencies (Predecessors)</p>
                {editing ? (
                  <div>
                    <input value={form.predecessor_plan_ids}
                      onChange={e => set('predecessor_plan_ids', e.target.value)}
                      placeholder="Plan IDs comma separated e.g. 10, 15, 22" className={inp} />
                    <p className="text-xs text-gray-400 mt-1">Enter Plan IDs of activities that must complete before this one starts</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-sm text-gray-800">
                      {activity.predecessor_plan_ids?.length
                        ? `Plan IDs: ${activity.predecessor_plan_ids.join(', ')}`
                        : 'No dependencies'}
                    </p>
                  </div>
                )}
              </div>
              {editing && (
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setEditing(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg">Cancel</button>
                  <button onClick={save} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg disabled:opacity-50">
                    <Save size={14} />{saving ? 'Saving...' : 'Save'}</button>
                </div>
              )}
              {!editing && (
                <button onClick={() => setEditing(true)} className="w-full px-4 py-2 border border-blue-300 text-blue-600 text-sm rounded-lg hover:bg-blue-50">
                  <Edit2 size={13} className="inline mr-1" />Edit Dates & PERT
                </button>
              )}
            </div>
          )}

          {tab === 'materials' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-500">{materials.length} materials mapped</p>
                <button onClick={autoLoadMaterials} disabled={saving}
                  className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  <RefreshCw size={11} className={saving ? 'animate-spin' : ''} />Load from Master
                </button>
              </div>
              {loadingTab ? (
                <div className="text-center py-8"><RefreshCw size={20} className="animate-spin text-blue-600 mx-auto mb-2" /><p className="text-sm text-gray-400">Loading...</p></div>
              ) : materials.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Package size={36} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">No materials mapped</p>
                  <p className="text-xs mt-1">Click "Load from Master" to auto-populate</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {materials.map((m: any) => (
                    <div key={m.req_id} className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800">{m.mat_name}</p>
                          <p className="text-xs text-gray-400">{m.mat_category} · {m.brand_name || 'Any brand'}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-sm font-bold text-gray-700">{Number(m.required_qty).toFixed(2)} {m.unit}</p>
                          <p className="text-xs text-amber-600">+{m.wastage_pct}% waste</p>
                          <p className="text-xs font-semibold text-blue-600">= {Number(m.qty_with_wastage).toFixed(2)} {m.unit}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'manpower' && (
            <div>
              <p className="text-xs text-gray-500 mb-3">{manpower.length} skills mapped</p>
              {loadingTab ? (
                <div className="text-center py-8"><RefreshCw size={20} className="animate-spin text-blue-600 mx-auto mb-2" /></div>
              ) : manpower.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Users size={36} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">No labour mapped</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {manpower.map((m: any) => (
                    <div key={m.req_id} className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{m.skill_name}</p>
                          <p className="text-xs text-gray-400">{m.trade_category}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-700">{m.workers_required} workers</p>
                          <span className={`text-xs px-2 py-0.5 rounded font-medium
                            ${m.source === 'OWN' ? 'bg-green-100 text-green-700' : m.source === 'SUBCONTRACT' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                            {m.source}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'machinery' && (
            <div>
              <p className="text-xs text-gray-500 mb-3">{machinery.length} machines mapped</p>
              {loadingTab ? (
                <div className="text-center py-8"><RefreshCw size={20} className="animate-spin text-blue-600 mx-auto mb-2" /></div>
              ) : machinery.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Truck size={36} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">No machinery mapped</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {machinery.map((m: any) => (
                    <div key={m.req_id} className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{m.mch_type}</p>
                          <p className="text-xs text-gray-400">Qty: {m.quantity}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${m.source === 'OWN' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {m.source}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Tri-state checkbox helper ─────────────────────────────────
const TriCheckbox = ({ applicable, total, onClick }: { applicable: number; total: number; onClick: (v: boolean) => void }) => {
  const allOn = applicable === total && total > 0;
  const someOn = applicable > 0 && applicable < total;
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(!allOn); }}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
        ${allOn ? 'border-blue-500 bg-blue-500 text-white' : someOn ? 'border-blue-400 bg-blue-100 text-blue-500' : 'border-gray-300 bg-white'}`}>
      {allOn && <CheckCircle size={11} />}
      {someOn && <span className="text-xs leading-none font-bold">–</span>}
    </button>
  );
};

// ── Reorder arrows ────────────────────────────────────────────
const ReorderBtns = ({ onUp, onDown, isFirst, isLast }: {
  onUp: () => void; onDown: () => void; isFirst: boolean; isLast: boolean;
}) => (
  <div className="flex flex-col gap-px flex-shrink-0">
    <button onClick={e => { e.stopPropagation(); onUp(); }} disabled={isFirst}
      className={`p-0.5 rounded transition-colors ${isFirst ? 'text-gray-200 cursor-default' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
      <ArrowUp size={11} />
    </button>
    <button onClick={e => { e.stopPropagation(); onDown(); }} disabled={isLast}
      className={`p-0.5 rounded transition-colors ${isLast ? 'text-gray-200 cursor-default' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}>
      <ArrowDown size={11} />
    </button>
  </div>
);

// ── Activity Row ──────────────────────────────────────────────
const ActivityRow = ({ act, onSelect, onToggle, onMoveUp, onMoveDown, showMove }: {
  act: Activity; onSelect: () => void; onToggle: (v: boolean) => void;
  onMoveUp: () => void; onMoveDown: () => void; showMove: boolean;
}) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg group transition-colors
    ${act.is_applicable ? 'hover:bg-blue-50' : 'opacity-40 hover:opacity-60'}`}>
    <button onClick={() => onToggle(!act.is_applicable)}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
        ${act.is_applicable ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 bg-white'}`}>
      {act.is_applicable && <CheckCircle size={11} />}
    </button>
    {showMove && (
      <div className="flex flex-col gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onMoveUp} className="text-gray-300 hover:text-gray-600"><ArrowUp size={10} /></button>
        <button onClick={onMoveDown} className="text-gray-300 hover:text-gray-600"><ArrowDown size={10} /></button>
      </div>
    )}
    <span className="text-xs font-mono text-gray-400 w-20 flex-shrink-0 truncate">{act.act_code}</span>
    <span className={`text-sm flex-1 min-w-0 truncate ${act.is_applicable ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
      {act.activity_name}
    </span>
    <div className="flex items-center gap-1 flex-shrink-0">
      {act.is_custom_activity && <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Custom</span>}
      {act.is_milestone && <Flag size={11} className="text-purple-500" />}
      {act.is_critical_path && <span className="text-red-500 text-xs">⚡</span>}
      {act.photo_required === 'MANDATORY' && <Camera size={11} className="text-rose-400" />}
      {act.quality_check_required && <Shield size={11} className="text-blue-400" />}
      {act.activity_type && act.activity_type !== 'ADMIN' && (
        <span className={`text-xs px-1.5 py-0.5 rounded ${TYPE_COLORS[act.activity_type] || ''}`}>
          {act.activity_type.slice(0, 3)}
        </span>
      )}
      {act.planned_start_date && (
        <span className="text-xs text-gray-400 hidden md:block">{act.planned_start_date.split('T')[0]}</span>
      )}
      <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[act.status] || ''}`}>
        {act.completion_pct > 0 ? `${act.completion_pct}%` : '—'}
      </span>
    </div>
    <button onClick={onSelect}
      className="p-1 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
      <ChevronRight size={14} />
    </button>
  </div>
);

// ── Modal discriminator ───────────────────────────────────────
type ModalType = 'stage' | 'process' | 'subprocess' | 'activity' | null;
interface PosItem { id: number; name: string; code?: string; }
interface ModalCtx {
  stageId?: number; stageName?: string;
  processId?: number; processName?: string;
  subProcessId?: number;
  existingItems?: PosItem[];
}

// ── Main Page ─────────────────────────────────────────────────
const ActivityPlanPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [plan, setPlan] = useState<Stage[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openStages, setOpenStages] = useState<number[]>([]);
  const [openProcs, setOpenProcs] = useState<number[]>([]);
  const [openSubs, setOpenSubs] = useState<number[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [modalCtx, setModalCtx] = useState<ModalCtx>({});
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [showApplicableOnly, setShowApplicableOnly] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/projects/', { params: { status: '' } });
      setProjects(r.data.data || []);
    };
    load();
  }, []);

  const loadPlan = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const [planRes, statsRes] = await Promise.all([
        api.get(`/project-plan/${projectId}/plan-grouped`),
        api.get(`/project-plan/${projectId}/plan-stats`),
      ]);
      setPlan(planRes.data.data || []);
      setStats(statsRes.data.data);
    } finally { setLoading(false); }
  }, []);

  const handleProjectChange = async (projectId: number) => {
    const proj = projects.find(p => p.project_id === projectId);
    setSelectedProject(proj || null);
    setPlan([]); setStats(null); setOpenStages([]); setOpenProcs([]); setOpenSubs([]);
    if (projectId) await loadPlan(projectId);
  };

  const generatePlan = async () => {
    if (!selectedProject) return;
    if (!window.confirm('Regenerate activity plan from master? Existing custom activities will be deleted.')) return;
    setGenerating(true);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/project-plan/${selectedProject.project_id}/generate-plan`);
      await loadPlan(selectedProject.project_id);
    } finally { setGenerating(false); }
  };

  // ── Toggle helpers ────────────────────────────────────────────
  const toggleActivity = async (planId: number, applicable: boolean) => {
    const { default: api } = await import('../../utils/api');
    await api.put(`/project-plan/activity/${planId}`, { is_applicable: applicable });
    setPlan(prev => prev.map(s => ({
      ...s,
      processes: s.processes.map(p => ({
        ...p,
        sub_processes: p.sub_processes.map(sp => ({
          ...sp,
          activities: sp.activities.map(a =>
            a.plan_id === planId ? { ...a, is_applicable: applicable } : a
          ),
        })),
      })),
    })));
  };

  const toggleStage = async (stageId: number, applicable: boolean) => {
    if (!selectedProject) return;
    const { default: api } = await import('../../utils/api');
    await api.patch(`/project-plan/${selectedProject.project_id}/toggle-stage/${stageId}`, { is_applicable: applicable });
    await loadPlan(selectedProject.project_id);
  };

  const toggleProcess = async (processId: number, applicable: boolean) => {
    if (!selectedProject) return;
    const { default: api } = await import('../../utils/api');
    await api.patch(`/project-plan/${selectedProject.project_id}/toggle-process/${processId}`, { is_applicable: applicable });
    setPlan(prev => prev.map(s => ({
      ...s,
      processes: s.processes.map(p => {
        if (p.process_id !== processId) return p;
        return {
          ...p,
          sub_processes: p.sub_processes.map(sp => ({
            ...sp,
            activities: sp.activities.map(a => ({ ...a, is_applicable: applicable })),
          })),
        };
      }),
    })));
  };

  const toggleSubProcess = async (subProcessId: number, applicable: boolean) => {
    if (!selectedProject) return;
    const { default: api } = await import('../../utils/api');
    await api.patch(`/project-plan/${selectedProject.project_id}/toggle-subprocess/${subProcessId}`, { is_applicable: applicable });
    setPlan(prev => prev.map(s => ({
      ...s,
      processes: s.processes.map(p => ({
        ...p,
        sub_processes: p.sub_processes.map(sp => {
          if (sp.sub_process_id !== subProcessId) return sp;
          return { ...sp, activities: sp.activities.map(a => ({ ...a, is_applicable: applicable })) };
        }),
      })),
    })));
  };

  // ── Reorder helpers ───────────────────────────────────────────
  const moveStage = async (si: number, dir: 'up' | 'down') => {
    const swapWith = dir === 'up' ? si - 1 : si + 1;
    if (swapWith < 0 || swapWith >= plan.length) return;
    const updates = [
      { stage_id: plan[si].stage_id, sequence_order: plan[swapWith].sequence_order },
      { stage_id: plan[swapWith].stage_id, sequence_order: plan[si].sequence_order },
    ];
    const { default: api } = await import('../../utils/api');
    await api.post('/activity/stages/reorder', { updates });
    if (selectedProject) await loadPlan(selectedProject.project_id);
  };

  const moveProcess = async (si: number, pi: number, dir: 'up' | 'down') => {
    const procs = plan[si].processes;
    const swapWith = dir === 'up' ? pi - 1 : pi + 1;
    if (swapWith < 0 || swapWith >= procs.length) return;
    const updates = [
      { process_id: procs[pi].process_id, sequence_order: procs[swapWith].sequence_order },
      { process_id: procs[swapWith].process_id, sequence_order: procs[pi].sequence_order },
    ];
    const { default: api } = await import('../../utils/api');
    await api.post('/activity/processes/reorder', { updates });
    if (selectedProject) await loadPlan(selectedProject.project_id);
  };

  const moveSubProcess = async (si: number, pi: number, subi: number, dir: 'up' | 'down') => {
    const subs = plan[si].processes[pi].sub_processes;
    const swapWith = dir === 'up' ? subi - 1 : subi + 1;
    if (swapWith < 0 || swapWith >= subs.length) return;
    const updates = [
      { sub_process_id: subs[subi].sub_process_id, sequence_order: subs[swapWith].sequence_order },
      { sub_process_id: subs[swapWith].sub_process_id, sequence_order: subs[subi].sequence_order },
    ];
    const { default: api } = await import('../../utils/api');
    await api.post('/activity/sub-processes/reorder', { updates });
    if (selectedProject) await loadPlan(selectedProject.project_id);
  };

  const moveActivity = async (si: number, pi: number, subi: number, ai: number, dir: 'up' | 'down') => {
    const acts = plan[si].processes[pi].sub_processes[subi].activities;
    const swapWith = dir === 'up' ? ai - 1 : ai + 1;
    if (swapWith < 0 || swapWith >= acts.length) return;
    const updates = [
      { plan_id: acts[ai].plan_id, sequence_order: acts[swapWith].sequence_order },
      { plan_id: acts[swapWith].plan_id, sequence_order: acts[ai].sequence_order },
    ];
    if (!selectedProject) return;
    const { default: api } = await import('../../utils/api');
    await api.post(`/project-plan/${selectedProject.project_id}/bulk-sequence`, { updates });
    await loadPlan(selectedProject.project_id);
  };

  const toggle = (arr: number[], setArr: React.Dispatch<React.SetStateAction<number[]>>, id: number) =>
    setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const filterAct = (act: Activity) => {
    if (typeFilter && act.activity_type !== typeFilter) return false;
    if (showApplicableOnly && !act.is_applicable) return false;
    if (searchQ && !act.activity_name.toLowerCase().includes(searchQ.toLowerCase()) &&
      !act.act_code?.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  };

  const totalActs = plan.reduce((s, st) => s + st.processes.reduce((ps, p) =>
    ps + p.sub_processes.reduce((ss, sp) => ss + sp.activities.length, 0), 0), 0);

  const openModal = (type: ModalType, ctx: ModalCtx = {}) => { setModalType(type); setModalCtx(ctx); };
  const closeModal = () => { setModalType(null); setModalCtx({}); };
  const onModalSaved = () => {
    closeModal();
    if (selectedProject) loadPlan(selectedProject.project_id);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* ── Modals ── */}
      {selectedActivity && selectedProject && (
        <ActivityDrawer
          activity={selectedActivity}
          projectId={selectedProject.project_id}
          onClose={() => setSelectedActivity(null)}
          onSaved={() => { loadPlan(selectedProject.project_id); setSelectedActivity(null); }}
        />
      )}
      {modalType === 'stage' && (
        <AddStageModal
          existingStages={plan.map(s => ({ id: s.stage_id, name: s.stage_name, code: s.stage_code }))}
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}
      {modalType === 'process' && modalCtx.stageId && (
        <AddProcessModal
          stageId={modalCtx.stageId}
          stageName={modalCtx.stageName || ''}
          existingProcesses={modalCtx.existingItems || []}
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}
      {modalType === 'subprocess' && modalCtx.processId && (
        <AddSubProcessModal
          processId={modalCtx.processId}
          processName={modalCtx.processName || ''}
          stageName={modalCtx.stageName || ''}
          existingSubProcesses={modalCtx.existingItems || []}
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}
      {modalType === 'activity' && selectedProject && (
        <AddActivityModal
          projectId={selectedProject.project_id}
          stages={plan}
          preStageId={modalCtx.stageId}
          preProcessId={modalCtx.processId}
          preSubProcessId={modalCtx.subProcessId}
          onClose={closeModal}
          onSaved={onModalSaved}
        />
      )}

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Activity Planning</h1>
            <p className="text-xs text-gray-400 mt-0.5">Stage → Process → Sub-Process → Activity hierarchy</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Select Project</label>
              <select
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[260px]"
                value={selectedProject?.project_id || ''}
                onChange={e => handleProjectChange(Number(e.target.value))}>
                <option value="">— Choose a project —</option>
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_code} · {p.project_name} ({p.building_type})
                  </option>
                ))}
              </select>
            </div>
            {selectedProject && (
              <div className="flex items-center gap-2">
                <button onClick={generatePlan} disabled={generating}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50">
                  <RefreshCw size={14} className={generating ? 'animate-spin' : ''} />
                  {generating ? 'Generating...' : 'Regenerate'}
                </button>
              </div>
            )}
          </div>
        </div>

        {stats && selectedProject && (
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${BLDG_COLORS[selectedProject.building_type] || 'bg-gray-500'}`}>
              {selectedProject.building_type}
            </div>
            {[
              { label: 'Total', val: totalActs, color: 'text-gray-700' },
              { label: 'Applicable', val: stats.total_applicable, color: 'text-blue-600' },
              { label: 'Completed', val: stats.completed, color: 'text-green-600' },
              { label: 'In Progress', val: stats.in_progress, color: 'text-amber-600' },
              { label: 'Delayed', val: stats.delayed, color: 'text-red-600' },
              { label: 'Milestones', val: stats.milestones, color: 'text-purple-600' },
              { label: 'Scheduled', val: stats.scheduled, color: 'text-indigo-600' },
              { label: 'Custom', val: stats.custom_activities, color: 'text-teal-600' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className={`text-sm font-bold ${s.color}`}>{s.val || 0}</p>
                <p className="text-xs text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {!selectedProject && (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <div className="text-center">
            <Settings size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-lg font-medium text-gray-500">Select a project to start activity planning</p>
            <p className="text-sm mt-1">Choose from the dropdown above</p>
          </div>
        </div>
      )}

      {selectedProject && (
        <>
          {/* Filter bar */}
          <div className="bg-white border-b border-gray-100 px-6 py-2.5 flex items-center gap-3 flex-shrink-0 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Search activities..."
                className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none">
              <option value="">All Types</option>
              {['STRUCTURAL', 'FINISHING', 'MEP', 'EXTERNAL', 'ADMIN'].map(t => <option key={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={showApplicableOnly} onChange={e => setShowApplicableOnly(e.target.checked)} className="w-4 h-4 rounded" />
              Applicable only
            </label>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => openModal('stage')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                <Plus size={12} /> Add Stage
              </button>
              <button onClick={() => openModal('activity')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700">
                <Plus size={12} /> Add Custom Activity
              </button>
              <p className="text-xs text-gray-400">☑=toggle · ↕=reorder · →=edit</p>
            </div>
          </div>

          {/* Stage tree */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 h-14 animate-pulse" />)}
              </div>
            ) : plan.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <AlertTriangle size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-base font-medium">No activities generated yet</p>
                <p className="text-sm mt-1 mb-4">Click "Regenerate" to load all master activities for this project</p>
                <button onClick={generatePlan} disabled={generating}
                  className="px-6 py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                  Generate Activity Plan
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {plan.map((stage, si) => {
                  const sOpen = openStages.includes(stage.stage_id);
                  const stageProgress = stage.activity_count > 0
                    ? Math.round((stage.completed_count / stage.activity_count) * 100) : 0;
                  const hasMatch = stage.processes.some(p =>
                    p.sub_processes.some(sp => sp.activities.some(filterAct)));
                  if ((typeFilter || searchQ || showApplicableOnly) && !hasMatch) return null;

                  return (
                    <div key={stage.stage_id}
                      className={`bg-white rounded-xl border overflow-hidden
                        ${stage.applicable_count > 0 ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>

                      {/* ── Stage header ── */}
                      <div className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50">
                        {/* Reorder arrows for Stage */}
                        <ReorderBtns
                          onUp={() => moveStage(si, 'up')}
                          onDown={() => moveStage(si, 'down')}
                          isFirst={si === 0}
                          isLast={si === plan.length - 1}
                        />
                        <button className="flex items-center gap-2 flex-1 text-left min-w-0"
                          onClick={() => toggle(openStages, setOpenStages, stage.stage_id)}>
                          {sOpen ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
                          <TriCheckbox
                            applicable={stage.applicable_count}
                            total={stage.activity_count}
                            onClick={v => toggleStage(stage.stage_id, v)}
                          />
                          <span className="text-xs font-mono text-gray-400 w-12 flex-shrink-0">{stage.stage_code}</span>
                          <span className="text-sm font-bold text-gray-800 flex-1 min-w-0 truncate">{stage.stage_name}</span>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                          <span className="text-gray-400">{stage.applicable_count}/{stage.activity_count}</span>
                          {stageProgress > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-12 bg-gray-100 rounded-full h-1.5">
                                <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${stageProgress}%` }} />
                              </div>
                              <span className="text-green-600">{stageProgress}%</span>
                            </div>
                          )}
                          {/* Stage + → Add Process */}
                          <button
                            onClick={e => { e.stopPropagation(); openModal('process', {
                              stageId: stage.stage_id, stageName: stage.stage_name,
                              existingItems: stage.processes.map(p => ({ id: p.process_id, name: p.process_name, code: p.process_code })),
                            }); }}
                            className="p-1 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            title="Add process to this stage">
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>

                      {/* ── Processes ── */}
                      {sOpen && (
                        <div className="border-t border-gray-50 px-3 py-2 space-y-1">
                          {stage.processes.map((proc, pi) => {
                            const pOpen = openProcs.includes(proc.process_id);
                            const pApplicable = proc.applicable_count ?? proc.sub_processes.reduce((s, sp) => s + sp.activities.filter(a => a.is_applicable).length, 0);
                            const pTotal = proc.activity_count ?? proc.sub_processes.reduce((s, sp) => s + sp.activities.length, 0);
                            const hasMatchP = proc.sub_processes.some(sp => sp.activities.some(filterAct));
                            if ((typeFilter || searchQ || showApplicableOnly) && !hasMatchP) return null;

                            return (
                              <div key={proc.process_id} className="ml-3">
                                {/* Process header */}
                                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                                  <ReorderBtns
                                    onUp={() => moveProcess(si, pi, 'up')}
                                    onDown={() => moveProcess(si, pi, 'down')}
                                    isFirst={pi === 0}
                                    isLast={pi === stage.processes.length - 1}
                                  />
                                  <button onClick={() => toggle(openProcs, setOpenProcs, proc.process_id)}
                                    className="flex items-center gap-2 flex-1 text-left min-w-0">
                                    {pOpen ? <ChevronDown size={13} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={13} className="text-gray-400 flex-shrink-0" />}
                                    <TriCheckbox
                                      applicable={pApplicable}
                                      total={pTotal}
                                      onClick={v => toggleProcess(proc.process_id, v)}
                                    />
                                    <span className="text-xs font-mono text-gray-400 w-16 flex-shrink-0">{proc.process_code}</span>
                                    <span className="text-sm font-medium text-gray-700 flex-1 min-w-0 truncate">{proc.process_name}</span>
                                  </button>
                                  <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-400">
                                    <span>{pApplicable}/{pTotal}</span>
                                    {/* Process + → Add Sub-Process */}
                                    <button
                                      onClick={e => { e.stopPropagation(); openModal('subprocess', {
                                        stageId: stage.stage_id, stageName: stage.stage_name,
                                        processId: proc.process_id, processName: proc.process_name,
                                        existingItems: proc.sub_processes.map(sp => ({ id: sp.sub_process_id, name: sp.sub_process_name, code: sp.sub_process_code })),
                                      }); }}
                                      className="p-1 text-gray-300 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                                      title="Add sub-process to this process">
                                      <Plus size={12} />
                                    </button>
                                  </div>
                                </div>

                                {/* Sub-processes */}
                                {pOpen && (
                                  <div className="ml-5 space-y-1">
                                    {proc.sub_processes.map((sub, subi) => {
                                      const subOpen = openSubs.includes(sub.sub_process_id);
                                      const sApplicable = sub.applicable_count ?? sub.activities.filter(a => a.is_applicable).length;
                                      const sTotal = sub.activity_count ?? sub.activities.length;
                                      const filteredActs = sub.activities.filter(filterAct);
                                      if ((typeFilter || searchQ || showApplicableOnly) && filteredActs.length === 0) return null;

                                      return (
                                        <div key={sub.sub_process_id}>
                                          {/* Sub-process header */}
                                          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50">
                                            <ReorderBtns
                                              onUp={() => moveSubProcess(si, pi, subi, 'up')}
                                              onDown={() => moveSubProcess(si, pi, subi, 'down')}
                                              isFirst={subi === 0}
                                              isLast={subi === proc.sub_processes.length - 1}
                                            />
                                            <button onClick={() => toggle(openSubs, setOpenSubs, sub.sub_process_id)}
                                              className="flex items-center gap-2 flex-1 text-left min-w-0">
                                              {subOpen ? <ChevronDown size={12} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />}
                                              <TriCheckbox
                                                applicable={sApplicable}
                                                total={sTotal}
                                                onClick={v => toggleSubProcess(sub.sub_process_id, v)}
                                              />
                                              <span className="text-xs font-mono text-gray-400 w-16 flex-shrink-0">{sub.sub_process_code}</span>
                                              <span className="text-sm text-gray-600 flex-1 min-w-0 truncate">{sub.sub_process_name}</span>
                                            </button>
                                            <div className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-400">
                                              <span>{sApplicable}/{sTotal}</span>
                                              {/* Sub-process + → Add Custom Activity */}
                                              <button
                                                onClick={e => { e.stopPropagation(); openModal('activity', { stageId: stage.stage_id, processId: proc.process_id, subProcessId: sub.sub_process_id }); }}
                                                className="p-1 text-gray-300 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                                                title="Add custom activity to this sub-process">
                                                <Plus size={12} />
                                              </button>
                                            </div>
                                          </div>

                                          {/* Activities */}
                                          {subOpen && (
                                            <div className="ml-8 border-l-2 border-gray-100 pl-2 py-1">
                                              {filteredActs.map((act, ai) => (
                                                <ActivityRow
                                                  key={act.plan_id}
                                                  act={act}
                                                  onSelect={() => setSelectedActivity(act)}
                                                  onToggle={v => toggleActivity(act.plan_id, v)}
                                                  onMoveUp={() => moveActivity(si, pi, subi, ai, 'up')}
                                                  onMoveDown={() => moveActivity(si, pi, subi, ai, 'down')}
                                                  showMove={filteredActs.length > 1}
                                                />
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityPlanPage;
